import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type LoaderMode = 'blocking' | 'background';

@Injectable({ providedIn: 'root' })
export class LoaderService {
  private blockingRequestCount = 0;
  private backgroundRequestCount = 0;

  private readonly backgroundShowDelayMs = 180;
  private readonly backgroundMinVisibleMs = 260;

  private backgroundVisibleSince = 0;
  private backgroundShowTimer: ReturnType<typeof setTimeout> | null = null;
  private backgroundHideTimer: ReturnType<typeof setTimeout> | null = null;

  public isLoading$ = new BehaviorSubject<boolean>(false);
  public isBlocking$ = new BehaviorSubject<boolean>(false);
  public isBackground$ = new BehaviorSubject<boolean>(false);

  constructor() {
    // Ensure we start with false state
    this.reset();
  }

  start(mode: LoaderMode = 'background') {
    if (mode === 'blocking') {
      this.blockingRequestCount++;
      this.isBlocking$.next(true);
      this.syncLoadingState();
      return;
    }

    this.backgroundRequestCount++;
    this.syncBackgroundIndicator();
    this.syncLoadingState();
  }

  stop(mode: LoaderMode = 'background') {
    if (mode === 'blocking') {
      this.blockingRequestCount = Math.max(this.blockingRequestCount - 1, 0);
      if (this.blockingRequestCount === 0) {
        this.isBlocking$.next(false);
      }
      this.syncBackgroundIndicator();
      this.syncLoadingState();
      return;
    }

    this.backgroundRequestCount = Math.max(this.backgroundRequestCount - 1, 0);
    this.syncBackgroundIndicator();
    this.syncLoadingState();
  }

  show() {
    this.start('background');
  }

  hide() {
    this.stop('background');
  }

  // Method to reset loader state if needed
  reset() {
    this.blockingRequestCount = 0;
    this.backgroundRequestCount = 0;
    this.backgroundVisibleSince = 0;
    this.clearTimers();

    this.isLoading$.next(false);
    this.isBlocking$.next(false);
    this.isBackground$.next(false);
  }

  private syncLoadingState() {
    this.isLoading$.next(this.isBlocking$.value || this.isBackground$.value);
  }

  private syncBackgroundIndicator() {
    this.clearBackgroundShowTimer();
    this.clearBackgroundHideTimer();

    if (this.backgroundRequestCount > 0) {
      if (this.isBackground$.value || this.isBlocking$.value) {
        return;
      }

      this.backgroundShowTimer = setTimeout(() => {
        if (this.backgroundRequestCount > 0 && this.blockingRequestCount === 0) {
          this.isBackground$.next(true);
          this.backgroundVisibleSince = Date.now();
          this.syncLoadingState();
        }
      }, this.backgroundShowDelayMs);
      return;
    }

    if (!this.isBackground$.value) {
      return;
    }

    const elapsed = Date.now() - this.backgroundVisibleSince;
    const remaining = Math.max(this.backgroundMinVisibleMs - elapsed, 0);

    this.backgroundHideTimer = setTimeout(() => {
      if (this.backgroundRequestCount === 0) {
        this.isBackground$.next(false);
        this.syncLoadingState();
      }
    }, remaining);
  }

  private clearTimers() {
    this.clearBackgroundShowTimer();
    this.clearBackgroundHideTimer();
  }

  private clearBackgroundShowTimer() {
    if (!this.backgroundShowTimer) {
      return;
    }

    clearTimeout(this.backgroundShowTimer);
    this.backgroundShowTimer = null;
  }

  private clearBackgroundHideTimer() {
    if (!this.backgroundHideTimer) {
      return;
    }

    clearTimeout(this.backgroundHideTimer);
    this.backgroundHideTimer = null;
  }
}
